import { HttpClient } from "@angular/common/http"
import { Injectable } from "@angular/core"
import { API_HOST } from "environments/environment"
import { Observable } from "rxjs"

// ==================== INTERFEJSY ====================

/**
 * Parametry zapytania o faktury z KSeF
 */
export interface KsefInvoiceQueryParams {
    /** Typ podmiotu: 'subject1' (wystawione), 'subject2' (otrzymane), 'subject3' (wszystkie) */
    subjectType?: 'subject1' | 'subject2' | 'subject3';
    /** Typ faktury: 'FA', 'FAP', 'RO', 'WEW', 'FP', etc. */
    invoiceType?: string;
    /** Data od (ISO 8601 datetime) */
    dateFrom?: string;
    /** Data do (ISO 8601 datetime) */
    dateTo?: string;
    /** Numer faktury */
    invoiceNumber?: string;
    /** NIP kontrahenta */
    subjectByIdentifier?: string;
    /** Rozmiar strony (10-250) */
    pageSize?: number;
    /** Offset strony (0-based) */
    pageOffset?: number;
}

/**
 * Dane podmiotu (sprzedawca/nabywca) w fakturze KSeF
 */
export interface KsefInvoiceSubject {
    identifier?: string;
    name?: string;
    countryCode?: string;
    identifierType?: string;
}

/**
 * Kod formularza faktury
 */
export interface KsefInvoiceFormCode {
    code?: string;
    variant?: string;
    version?: string;
}

/**
 * Pojedyncza faktura z KSeF - rzeczywista struktura z API
 */
export interface KsefInvoice {
    /** Numer KSeF faktury (format: NIP-YYYYMMDD-identyfikator) */
    ksefNumber: string;
    /** Numer faktury nadany przez wystawcę */
    invoiceNumber: string;
    /** Data wystawienia faktury (YYYY-MM-DD) */
    issueDate: string;
    /** Data fakturowania (ISO 8601 datetime) */
    invoicingDate: string;
    /** Data wpływu do KSeF (ISO 8601 datetime) */
    acquisitionDate: string;
    /** Data trwałego zapisu w KSeF (ISO 8601 datetime) */
    permanentStorageDate: string;
    /** Dane sprzedawcy */
    seller: KsefInvoiceSubject;
    /** Dane nabywcy */
    buyer: KsefInvoiceSubject;
    /** Kwota netto */
    netAmount: number;
    /** Kwota brutto */
    grossAmount: number;
    /** Kwota VAT */
    vatAmount: number;
    /** Waluta (PLN, EUR, USD, etc.) */
    currency: string;
    /** Tryb wystawienia: 'Online' | 'Offline' */
    invoicingMode: 'Online' | 'Offline';
    /** Typ faktury: 'Vat' | 'VatMargin' | etc. */
    invoiceType: string;
    /** Kod formularza (FA, FAP, RO, WEW, FP) */
    formCode: KsefInvoiceFormCode;
    /** Czy jest to faktura samowystawiona */
    isSelfInvoicing: boolean;
    /** Czy faktura ma załączniki */
    hasAttachment: boolean;
    /** Hash faktury (SHA-256, base64) */
    invoiceHash: string;
    /** Dodatkowe podmioty (third subjects) */
    thirdSubjects: KsefInvoiceSubject[];
}

/**
 * Response z query faktur z KSeF - rzeczywista struktura z API
 */
export interface KsefInvoicesQueryResponse {
    success: boolean;
    message: string;
    data: {
        invoices: KsefInvoice[];
        pagination: {
            pageSize: number;
            pageOffset: number;
            hasMore: boolean;
            isTruncated: boolean;
            count: number;
        };
    };
}

export type KsefConnectionType = 'TOKEN' | 'CERTIFICATE';

export interface SetKsefCertificatePayload {
    certificatePem: string;
    privateKeyPem: string;
    privateKeyPassphrase: string;
}

@Injectable({
    providedIn: 'root'
})
export class KSeFApiService {

    constructor(private http: HttpClient) { }

    /**
     * Pobiera status połączenia z KSeF
     */
    public get() {
        return this.http.get(API_HOST + `ksef/status`)
    }

    /**
     * Inicjalizuje nową sesję KSeF
     */
    public initSession() {
        return this.http.post(API_HOST + `ksef/init-token`, {})
    }

    /**
     * Ustawia token KSeF
     */
    public setToken(token: string) {
        return this.http.post(API_HOST + `ksef/set-token`, { token })
    }

    /**
     * Ustawia połączenie certyfikatem KSeF
     */
    public setCertificate(payload: SetKsefCertificatePayload) {
        return this.http.post(API_HOST + 'ksef/set-certificate', payload);
    }

    /**
     * Dezaktywuje połączenie z KSeF (usuwa tokeny, ustawia isAuthorized=false)
     */
    public deactivateConnection() {
        return this.http.delete(API_HOST + `ksef/connection`)
    }

    /**
     * Wysłanie faktury do KSeF (LEGACY - online mode, synchroniczna wysyłka)
     * @deprecated Użyj sendInstant() - instant send jest aktualnie aktywnym trybem
     */
    public sendInvoice(invoiceId: string) {
        return this.http.post(API_HOST + `ksef/send-invoice`, { id: invoiceId })
    }

    /**
     * Wysyła fakturę do KSeF w trybie instant (online session)
     * Mapuje do XML, szyfruje i wysyła od razu.
     * Jeśli KSeF nie odpowie w 60s, cron dokończy sprawdzanie statusu.
     */
    public sendInstant(invoiceId: string) {
        return this.http.post(API_HOST + `ksef/send-instant`, { id: invoiceId })
    }

    /**
     * Oznacza fakturę do wysyłki w trybie batch
     * @deprecated Batch mode tymczasowo wyłączony - używaj sendInstant()
     * Instant response - faktura zostanie wysłana w ciągu 30 minut przez cron
     */
    public sendToBatch(invoiceId: string) {
        // UWAGA: Endpoint /ksef/send jest teraz przekierowany na instant send
        return this.http.post(API_HOST + `ksef/send`, { id: invoiceId })
    }

    /**
     * Pobiera listę faktur z KSeF z paginacją i filtrowaniem
     * 
     * @param params Parametry zapytania
     * @returns Observable z listą faktur i danymi paginacji
     */
    public queryInvoices(params: KsefInvoiceQueryParams): Observable<KsefInvoicesQueryResponse> {
        return this.http.post<KsefInvoicesQueryResponse>(
            API_HOST + 'ksef/invoices/query',
            params
        );
    }

    /**
     * Pobiera fakturę z KSeF po numerze KSeF
     * 
     * @param ksefNumber Numer KSeF faktury (format: NIP-YYYYMMDD-identyfikator)
     * @returns Observable z danymi faktury (XML)
     */
    public getInvoiceByKsefNumber(ksefNumber: string) {
        return this.http.get(API_HOST + `ksef/invoices/${ksefNumber}`);
    }

    /**
     * Synchronizuje faktury z KSeF do tabeli Document (Unified Document Store)
     * Pobiera zarówno faktury wystawione (przychody) jak i otrzymane (koszty)
     * Po synchronizacji faktury są dostępne w standardowym listingu dokumentów z source='ksef'
     * 
     * @param options Opcje synchronizacji
     * @returns Observable z wynikiem synchronizacji
     */
    public syncInvoices(options?: {
        full?: boolean;
        subjectTypes?: ('subject1' | 'subject2' | 'subject3')[];
        dateFrom?: string;
        dateTo?: string;
    }) {
        return this.http.post(API_HOST + 'ksef/sync', options || {});
    }

    /**
     * Pobiera XML faktury z KSeF po numerze KSeF
     * XML jest zapisywany na serwerze w Document.ksefMetadata.rawXml
     * 
     * @param ksefNumber Numer KSeF faktury
     * @returns Observable z XML faktury
     */
    public getInvoiceXml(ksefNumber: string) {
        return this.http.get<{
            success: boolean;
            message: string;
            data: string; // XML content
        }>(API_HOST + `ksef/invoices/${ksefNumber}`);
    }
}